/**
 * MobileAppBanner - Fixed bottom banner promoting the Nordex mobile app.
 *
 * Features:
 * - Fixed to bottom of viewport with glass effect
 * - Shows on mobile devices (iOS and Android)
 * - Platform-aware branding (App Store / Play Store)
 * - Dismissible with localStorage persistence (button appears after 6 seconds)
 * - Clean Apple-style glassmorphism design
 * - Smooth expand animation when dismiss button appears
 */

import { useState, useEffect, useCallback } from "react";
import {
  getPlatform,
  isNordexAppAvailable,
  isMobileAppBannerDismissed,
  dismissMobileAppBanner,
  openNordexApp,
  NORDEX_APP_LINKS,
  type Platform,
} from "@/lib/mobile-wallet-utils";
import { trackAppDownloadClick } from "@/lib/track-app-download";
import { cn } from "@/lib/utils";

// Apple logo SVG icon component
const AppleIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 384 512"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

// Google Play logo SVG icon component
const PlayStoreIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 512 512"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
  </svg>
);

// Platform-aware store button
const StoreButton = ({ platform, className }: { platform: Platform; className?: string }) => {
  const isAndroid = platform === "android";
  
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 bg-white text-black px-2.5 py-1.5 rounded-lg",
        className
      )}
    >
      {isAndroid ? (
        <PlayStoreIcon className="h-4 w-4" />
      ) : (
        <AppleIcon className="h-4 w-4" />
      )}
      <div className="flex flex-col items-start leading-tight">
        <span className="text-[9px] font-medium">Download Now</span>
        <span className="text-[11px] font-semibold -mt-0.5">
          {isAndroid ? "Play Store" : "App Store"}
        </span>
      </div>
    </div>
  );
};

// Timer duration before dismiss button appears (in seconds)
const DISMISS_TIMER_SECONDS = 6;

export const MobileAppBanner = () => {
  const [isDismissed, setIsDismissed] = useState(true); // Start hidden to prevent flash
  const [platform, setPlatform] = useState<"ios" | "android" | "desktop">("desktop");
  const [canDismiss, setCanDismiss] = useState(false);

  useEffect(() => {
    // Check platform and dismissal state on mount
    const currentPlatform = getPlatform();
    setPlatform(currentPlatform);
    setIsDismissed(isMobileAppBannerDismissed());
  }, []);

  // Timer for enabling dismiss button
  useEffect(() => {
    if (isDismissed || platform === "desktop") return;

    const timer = setTimeout(() => {
      setCanDismiss(true);
    }, DISMISS_TIMER_SECONDS * 1000);

    return () => clearTimeout(timer);
  }, [isDismissed, platform]);

  const handleDismiss = useCallback(() => {
    if (!canDismiss) return;
    dismissMobileAppBanner();
    setIsDismissed(true);
  }, [canDismiss]);

  const handleDownload = useCallback(() => {
    const destination =
      platform === "ios"
        ? ("ios_app_store" as const)
        : NORDEX_APP_LINKS.android.includes("play.google.com")
          ? ("google_play" as const)
          : ("android_apk" as const);
    trackAppDownloadClick(destination, "mobile_banner");
    openNordexApp();
  }, [platform]);

  // Don't show on desktop
  if (platform === "desktop") {
    return null;
  }

  // Don't show if dismissed
  if (isDismissed) {
    return null;
  }

  // Don't show if no app available for this platform
  if (!isNordexAppAvailable(platform)) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100]">
      {/* Glass effect container with rounded top corners */}
      <div className="bg-black/80 backdrop-blur-xl border-t border-white/10 rounded-t-2xl shadow-2xl">
        {/* Safe area padding for iPhone home indicator */}
        <div className={cn(
          "px-4 pt-5 safe-area-bottom transition-all duration-300 ease-out",
          canDismiss ? "pb-4" : "pb-8"
        )}>
          {/* Main clickable area - content first */}
          <button
            onClick={handleDownload}
            className="w-full flex items-center gap-3 text-left"
          >
            {/* Nordex icon */}
            <div className="h-12 w-12 flex-shrink-0 rounded-xl bg-[#0a0a0a] border border-white/10 flex items-center justify-center p-2 shadow-lg">
              <img
                src="/nordex_icon_white.svg"
                alt="Nordex"
                className="h-full w-full object-contain"
              />
            </div>

            {/* Text content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-base whitespace-nowrap">
                Get the best experience
              </h3>
              <p className="text-white/50 text-sm">
                Download the Nordex app for {platform === "android" ? "Android" : "iOS"}
              </p>
            </div>

            {/* Store button - platform aware */}
            <StoreButton platform={platform} className="flex-shrink-0" />
          </button>

          {/* Dismiss button - full width frosted glass, smooth expand animation */}
          <div
            className={cn(
              "overflow-hidden transition-all duration-300 ease-out",
              canDismiss ? "max-h-16 opacity-100 mt-3" : "max-h-0 opacity-0 mt-0"
            )}
          >
            <button
              onClick={handleDismiss}
              className="w-full py-2.5 rounded-full bg-white/5 backdrop-blur-md border border-white/10 
                         text-white/50 text-xs font-medium transition-all
                         hover:bg-white/10 hover:text-white/70 active:scale-[0.98]"
            >
              Continue on web
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
